import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
    Alert,
    Button,
    Card,
    DatePicker,
    Drawer,
    Empty,
    Form,
    Image,
    Input,
    InputNumber,
    List,
    message,
    Modal,
    Popconfirm,
    Select,
    Space,
    Spin,
    Table,
    Tag,
    Tooltip,
    Typography,
    Upload,
} from 'antd';
import {
    CheckCircleOutlined,
    ClockCircleOutlined,
    DeleteOutlined,
    DownloadOutlined,
    ExclamationCircleOutlined,
    EyeOutlined,
    FilePdfOutlined,
    FileImageOutlined,
    FileSearchOutlined,
    InboxOutlined,
    ReloadOutlined,
    SearchOutlined,
    ThunderboltOutlined,
} from '@ant-design/icons';
import { flightLogRecognitionApi } from './api';
import ToolTraceViewer from './ToolTraceViewer';

const { Text } = Typography;
const { RangePicker } = DatePicker;
const { Dragger } = Upload;

const MAX_FILES_PER_BATCH = 20;
const UPLOAD_RESULT_STORAGE_KEY = 'flightLogRecognition.lastUploadResult';

// 表格垂直滚动：viewport 高度减去固定占用，剩下的留给 Table body 滚动条。
// 这个数是个估算（标题栏 + tab 头 + Card padding + Dragger + 筛选区 + 工具栏 + 表头 + 分页器），
// 实测调整。同 FlightMonitor/FlightListView 的 FIXED_OFFSET 思路一致。
const TABLE_SCROLL_FIXED_OFFSET = 600;
const MAX_SIZE_MB = 30;
const ALLOWED_EXT = ['.pdf', '.gif', '.jpg', '.jpeg', '.png', '.webp'];

const TEXT = {
    pending: '\u5f85\u8bc6\u522b',
    recognizing: '\u8bc6\u522b\u4e2d',
    parsed: '\u5df2\u8bc6\u522b',
    reviewRequired: '\u5f85\u590d\u6838',
    failed: '\u8bc6\u522b\u5931\u8d25',
    confirmed: '\u5df2\u786e\u8ba4',
    rejectedStatus: '\u5df2\u62d2\u7edd',
    status: '\u72b6\u6001',
    sourceFile: '\u6587\u4ef6',
    confidence: '\u7f6e\u4fe1\u5ea6',
    actions: '\u64cd\u4f5c',
    view: '\u67e5\u770b',
    flightNo: '\u822a\u73ed\u53f7',
    aircraftReg: '\u673a\u53f7',
    // 机号筛选：可多选 + 支持只输片段（如 WFF），语义与燃油明细列表的机号筛选一致。
    aircraftRegFilter: '\u673a\u53f7\uff08\u53ef\u591a\u9009\uff0c\u652f\u6301\u6a21\u7cca\uff09',
    airport: '\u673a\u573a',
    fileName: '\u6587\u4ef6\u540d',
    reset: '\u91cd\u7f6e',
    search: '\u67e5\u8be2',
    uploadPdf: '\u4e0a\u4f20PDF/\u56fe\u7247',
    uploadText: '\u70b9\u51fb\u6216\u62d6\u62fdPDF/\u56fe\u7247\u5230\u6b64\u5904\u4e0a\u4f20\u8bc6\u522b',
    uploadHint: `\u652f\u6301PDF/GIF/JPG/PNG/WEBP\uff0c\u5355\u6279\u6700\u591a ${MAX_FILES_PER_BATCH} \u4e2a\uff0c\u5355\u4e2a\u6587\u4ef6\u6700\u5927 ${MAX_SIZE_MB} MB`,
    uploading: '\u4e0a\u4f20\u4e2d...',
    uploadResult: '\u4e0a\u4f20\u7ed3\u679c',
    accepted: '\u5df2\u63a5\u6536',
    rejected: '\u5df2\u62d2\u7edd',
    duplicate: '\u5df2\u5b58\u5728',
    originalStatus: '\u539f\u72b6\u6001',
    viewOriginal: '\u67e5\u770b\u539f\u8bb0\u5f55',
    duplicateOf: '\u91cd\u590d\u4e8e',
    uploadedAt: '\u4e0a\u4f20\u4e8e',
    showLastUploadResult: '\u4e0a\u6b21\u4e0a\u4f20\u7ed3\u679c',
    preCheckTitle: '\u4e0a\u4f20\u9884\u89c8\u4e0e\u67e5\u91cd',
    preCheckLoading: '\u6b63\u5728\u8ba1\u7b97\u6587\u4ef6\u54c8\u5e0c\u5e76\u67e5\u91cd...',
    preCheckSummary: (total, fresh, dup) => `\u5171 ${total} \u4e2a\uff1a${fresh} \u4e2a\u65b0\u6587\u4ef6\uff0c${dup} \u4e2a\u5df2\u5b58\u5728`,
    preCheckFreshSection: '\u65b0\u6587\u4ef6\uff08\u5c06\u4e0a\u4f20\uff09',
    preCheckDupSection: '\u5df2\u5b58\u5728\uff08\u5c06\u8df3\u8fc7\uff09',
    preCheckUploadFresh: (n) => `\u4ec5\u4e0a\u4f20\u65b0\u6587\u4ef6 (${n})`,
    preCheckUploadAll: (n) => `\u5168\u90e8\u63d0\u4ea4 (${n})\uff0c\u91cd\u590d\u7531\u540e\u7aef\u53bb\u91cd`,
    preCheckCancel: '\u53d6\u6d88',
    preCheckFailed: '\u9884\u89c8\u67e5\u91cd\u5931\u8d25\uff0c\u5c06\u8d70\u4f20\u7edf\u4e0a\u4f20\u8def\u5f84',
    dateUtcSortTip: '\u6309\u822a\u6bb5\u65e5\u671f\u6392\u5e8f\uff08\u9ed8\u8ba4\u5012\u5e8f\uff0c\u70b9\u51fb\u5207\u6362\u6b63\u5e8f\uff09',
    emptyRecords: '\u6682\u65e0flight log\u8bc6\u522b\u8bb0\u5f55',
    totalPrefix: '\u5171',
    totalSuffix: '\u6761',
    drawerTitle: 'flight log\u8bc6\u522b\u8be6\u60c5',
    detailLoadFailed: '\u8be6\u60c5\u52a0\u8f7d\u5931\u8d25',
    listLoadFailed: '\u5217\u8868\u52a0\u8f7d\u5931\u8d25',
    uploadFailed: '\u4e0a\u4f20\u5931\u8d25',
    uploadSuccess: '\u4e0a\u4f20\u6210\u529f\uff0c\u5df2\u8fdb\u5165\u8bc6\u522b\u961f\u5217',
    invalidFileType: '\u4ec5\u652f\u6301PDF/GIF/JPG/PNG/WEBP\u6587\u4ef6',
    invalidFileSize: `\u6587\u4ef6\u4e0d\u80fd\u8d85\u8fc7 ${MAX_SIZE_MB} MB`,
    batchTruncated: `\u5355\u6279\u6700\u591a\u4e0a\u4f20 ${MAX_FILES_PER_BATCH} \u4e2a\u6587\u4ef6`,
    reExtract: '\u91cd\u65b0\u8bc6\u522b',
    reExtractQueued: '\u5df2\u52a0\u5165\u91cd\u65b0\u8bc6\u522b\u961f\u5217',
    reExtractFailed: '\u91cd\u65b0\u8bc6\u522b\u5931\u8d25',
    deleteAction: '\u5220\u9664',
    deleteConfirmTitle: '\u786e\u5b9a\u5220\u9664\u8fd9\u6761\u8bc6\u522b\u8bb0\u5f55\uff1f',
    deleteConfirmDesc: '\u5220\u9664\u540e\u4e0d\u4f1a\u51fa\u73b0\u5728\u5217\u8868\u91cc\uff0c\u5982\u9700\u6062\u590d\u8bf7\u8054\u7cfb\u7ba1\u7406\u5458\u3002',
    deleteSuccess: '\u8bb0\u5f55\u5df2\u5220\u9664',
    deleteFailed: '\u5220\u9664\u5931\u8d25',
    deleteOk: '\u5220\u9664',
    deleteCancel: '\u53d6\u6d88',
    batchDeleteAction: '\u6279\u91cf\u5220\u9664',
    exportFiltered: '\u5bfc\u51fa\u5f53\u524d\u7b5b\u9009',
    exportBatch: '\u5bfc\u51fa\u9009\u4e2d',
    exporting: '\u5bfc\u51fa\u4e2d...',
    exportSuccess: '\u5bfc\u51fa\u6210\u529f',
    exportFailed: '\u5bfc\u51fa\u5931\u8d25',
    exportEmpty: '\u5f53\u524d\u7b5b\u9009\u4e0b\u6ca1\u6709\u8bb0\u5f55\uff0c\u65e0\u6cd5\u5bfc\u51fa',
    exportSourceFiles: '\u6253\u5305\u6e90\u6587\u4ef6',
    exportSourceBatch: '\u6253\u5305\u6e90\u6587\u4ef6',
    exportSourceSuccess: '\u6253\u5305\u5b8c\u6210\uff0c\u5f00\u59cb\u4e0b\u8f7d',
    exportSourceFailed: '\u6253\u5305\u6e90\u6587\u4ef6\u5931\u8d25',
    exportSourcePreparing: '\u6b63\u5728\u6253\u5305\u6e90\u6587\u4ef6...',
    batchDeleteSelected: (count) => `\u5df2\u9009 ${count} \u6761`,
    batchDeleteConfirmTitle: (count) => `\u786e\u5b9a\u5220\u9664\u9009\u4e2d\u7684 ${count} \u6761\u8bb0\u5f55\uff1f`,
    batchDeleteConfirmDesc: '\u5220\u9664\u540e\u4e0d\u4f1a\u51fa\u73b0\u5728\u5217\u8868\u91cc\uff0c\u5982\u9700\u6062\u590d\u8bf7\u8054\u7cfb\u7ba1\u7406\u5458\u3002',
    batchDeleteSuccess: (count) => `\u5df2\u5220\u9664 ${count} \u6761\u8bb0\u5f55`,
    batchDeletePartial: (deleted, failed) => `\u5df2\u5220\u9664 ${deleted} \u6761\uff0c${failed} \u6761\u672a\u5904\u7406\uff08\u53ef\u80fd\u5df2\u88ab\u522b\u4eba\u5220\u9664\uff09`,
    batchDeleteFailed: '\u6279\u91cf\u5220\u9664\u5931\u8d25',
    clearSelection: '\u53d6\u6d88\u9009\u4e2d',
    close: '\u5173\u95ed',
    rawResult: '\u539f\u59cb\u8bc6\u522b\u7ed3\u679c',
    sourcePreview: '\u539f\u59cb\u6587\u4ef6',
    previewLoadFailed: '\u539f\u59cb\u6587\u4ef6\u9884\u89c8\u52a0\u8f7d\u5931\u8d25',
    reviewFields: '\u8bc6\u522b\u5b57\u6bb5\u590d\u6838',
    saveReview: '\u4fdd\u5b58\u4fee\u6539',
    confirmReview: '\u786e\u8ba4\u590d\u6838',
    reviewSaveSuccess: '\u590d\u6838\u4fee\u6539\u5df2\u4fdd\u5b58',
    reviewConfirmSuccess: '\u590d\u6838\u5df2\u786e\u8ba4',
    reviewFailed: '\u590d\u6838\u4fdd\u5b58\u5931\u8d25',
    reviewNotes: '\u590d\u6838\u5907\u6ce8',
    reviewReason: '\u5f85\u590d\u6838\u539f\u56e0',
    noReviewReason: '\u672a\u8fd4\u56de\u5f85\u590d\u6838\u539f\u56e0',
    majorMaintenance: '\u91cd\u5927\u7ef4\u4fee',
    // T14 \u8bc6\u522b\u9875\u8868\u683c "\u6765\u6e90" \u5217\u4e0e\u8be6\u60c5\u62bd\u5c49\u6eaf\u6e90\u4fe1\u606f
    sourceColumn: '\u6765\u6e90',
    sourceManualUpload: '\u4eba\u5de5\u4e0a\u4f20',
    sourceExternalCrawl: '\u81ea\u52a8\u6293\u53d6',
    sourceCrawlInfoTitle: '\u6765\u6e90\uff1a\u5916\u90e8\u6293\u53d6',
    sourceCrawlLinkText: (id, site, dateKey) => `\u6293\u53d6\u8bb0\u5f55 #${id}\uff08${site} / ${dateKey}\uff09`,
    sourceCrawlDeleted: '\u539f\u6293\u53d6\u8bb0\u5f55\u5df2\u5220\u9664',
    // \u5bf9\u9f50/\u8865\u5168\uff08S7\uff09\u4e0e\u201c\u63a8\u201d\u6807\u8bb0\uff08S1-S5\uff09
    reconcile: '\u5bf9\u9f50/\u8865\u5168',
    reconciling: '\u5bf9\u9f50\u4e2d...',
    reconcileSuccess: (n) => `\u5bf9\u9f50\u5b8c\u6210\uff0c\u5171\u8865\u5168/\u66f4\u65b0 ${n} \u5904`,
    reconcileNoChange: '\u5bf9\u9f50\u5b8c\u6210\uff0c\u65e0\u9700\u8865\u5168\uff08\u5df2\u662f\u6700\u65b0\uff09',
    reconcileFailed: '\u5bf9\u9f50\u5931\u8d25',
    derivedTag: '\u63a8',
    derivedTagTip: '\u7cfb\u7edf\u63a8\u7b97/\u8865\u5168\u503c\uff08\u975e\u8868\u5355\u5b9e\u8bfb\uff09',
    fleetEmptyTip: '\u673a\u961f\u4e3b\u6570\u636e\u4e3a\u7a7a\uff0c\u6682\u53ea\u8bfb\u5c55\u793a\u73b0\u6709\u503c',
    // \u91cd\u590d\u5355\u636e\u53bb\u91cd\uff08dedup\uff09\uff1a\u88ab\u53d6\u4ee3\u8bb0\u5f55\u6807\u8bc6 + \u8df3\u4e3b\u8bb0\u5f55 + \u4eba\u5de5\u7ffb\u6848
    dedupTag: '\u91cd\u590d\uff08\u5df2\u53bb\u91cd\uff09',  // 重复（已去重）
    dedupTagShort: '\u91cd\u590d',  // 重复（列表内紧凑标签）
    dedupTagTip: '\u4e0e\u4e3b\u8bb0\u5f55\u4e3a\u540c\u4e00\u5f20\u5355\u636e\uff08\u540c \u673a\u53f7/\u65e5\u671f/TL No.\uff09\uff0c\u5df2\u88ab\u53bb\u91cd\u53d6\u4ee3\uff0c\u4e0d\u8fdb\u7ed3\u7b97\u4e0e\u822a\u8ff9\u94fe\u3002',
    dedupDrawerAlert: '\u672c\u8bb0\u5f55\u4e3a\u91cd\u590d\u5355\u636e\uff0c\u5df2\u88ab\u53bb\u91cd\u53d6\u4ee3\uff08\u4e0d\u8fdb\u7ed3\u7b97/\u822a\u8ff9\u94fe\uff09\u3002',
    dedupGotoMain: '\u67e5\u770b\u4e3b\u8bb0\u5f55',
    dedupRestore: '\u8bbe\u4e3a\u4e3b\u8bb0\u5f55',
    dedupRestoreConfirmTitle: '\u628a\u672c\u6761\u8bbe\u4e3a\u53bb\u91cd\u4e3b\u8bb0\u5f55\uff1f',
    dedupRestoreConfirmDesc: '\u5c06\u64a4\u9500\u672c\u6761\u7684\u300c\u5df2\u53d6\u4ee3\u300d\u3001\u6062\u590d\u5176\u53c2\u4e0e\u5bf9\u9f50\uff0c\u5e76\u628a\u540c\u7ec4\u5176\u4f59\u8bb0\u5f55\u6539\u4e3a\u5df2\u62d2\u7edd\u3002',
    dedupRestoreSuccess: '\u5df2\u8bbe\u4e3a\u4e3b\u8bb0\u5f55',
    dedupRestoreFailed: '\u7ffb\u6848\u5931\u8d25',
    // 作废页（page_voided）：整页划线/VOID/作废 → 已拒绝；展示靠 record.page_voided 布尔字段（非 review_reasons），人工可「取消作废」翻案。
    voidedTag: '\u4f5c\u5e9f\u9875',
    voidedTagShort: '\u4f5c\u5e9f',
    voidedTagTip: '\u8be5\u9875\u88ab\u8bc6\u522b\u4e3a\u6574\u9875\u4f5c\u5e9f\uff08\u6574\u9875\u5212\u7ebf / VOID / CANCELLED / \u4f5c\u5e9f\u7ae0\uff09\uff0c\u5df2\u5f52\u4e3a\u300c\u5df2\u62d2\u7edd\u300d\uff0c\u4e0d\u8fdb\u7ed3\u7b97\u4e0e\u822a\u8ff9\u94fe\u3002',
    voidedDrawerAlert: '\u672c\u8bb0\u5f55\u88ab\u8bc6\u522b\u4e3a\u4f5c\u5e9f\u9875\uff08\u6574\u9875\u5212\u7ebf / VOID / \u4f5c\u5e9f\uff09\uff0c\u5df2\u5f52\u4e3a\u300c\u5df2\u62d2\u7edd\u300d\uff0c\u4e0d\u8fdb\u7ed3\u7b97 / \u822a\u8ff9\u94fe\u3002\u5982\u7cfb\u8bef\u5224\uff0c\u53ef\u300c\u53d6\u6d88\u4f5c\u5e9f\u300d\u7ffb\u6848\u3002',
    unvoid: '\u53d6\u6d88\u4f5c\u5e9f',
    unvoidConfirmTitle: '\u53d6\u6d88\u672c\u9875\u7684\u4f5c\u5e9f\u6807\u8bb0\uff1f',
    unvoidConfirmDesc: '\u5c06\u6e05\u9664\u4f5c\u5e9f\u6807\u8bb0\u5e76\u4ece\u300c\u5df2\u62d2\u7edd\u300d\u590d\u6d3b\uff0c\u8bb0\u5f55\u91cd\u65b0\u53c2\u4e0e\u5bf9\u9f50\uff1b\u4eba\u5de5\u5df2\u6539\u5b57\u6bb5\u4fdd\u7559\u3002',
    unvoidSuccess: '\u5df2\u53d6\u6d88\u4f5c\u5e9f',
    unvoidFailed: '\u53d6\u6d88\u4f5c\u5e9f\u5931\u8d25',
    // 已拒绝（重复被取代）记录：隐藏保存/确认复核，引导先「设为主记录」翻案。
    rejectedReviewHint: '\u8be5\u8bb0\u5f55\u5df2\u62d2\u7edd\uff08\u91cd\u590d\u88ab\u53d6\u4ee3\uff09\uff0c\u9700\u590d\u6838\u8bf7\u5148\u300c\u8bbe\u4e3a\u4e3b\u8bb0\u5f55\u300d\u7ffb\u6848',
    // 已拒绝（作废页）记录：隐藏保存/确认复核，引导先「取消作废」翻案。
    voidedReviewHint: '\u8be5\u8bb0\u5f55\u4e3a\u4f5c\u5e9f\u9875\uff08\u5df2\u62d2\u7edd\uff09\uff0c\u9700\u590d\u6838\u8bf7\u5148\u300c\u53d6\u6d88\u4f5c\u5e9f\u300d\u7ffb\u6848',
};

// \u63a8\u7b97\u6765\u6e90\u4ee3\u7801 \u2192 \u4e2d\u6587\u8bf4\u660e\uff08\u201c\u63a8\u201d\u6807\u7b7e\u60ac\u6d6e\u63d0\u793a\u7528\uff09\u3002
const RECON_SOURCE_LABELS = {
    fleet_whitelist_snap: '\u673a\u53f7\u5438\u9644\u5230\u673a\u961f\u6210\u5458',
    back_calc: '\u540c\u5f20\u65f6\u95f4\u5012\u63a8',
    prev_leg_arrival: '\u53d6\u4e0a\u4e00\u6bb5\u5230\u8fbe\u5730',
    next_leg_departure: '\u53d6\u4e0b\u4e00\u6bb5\u51fa\u53d1\u5730',
    prev_leg_on_block_fuel: '\u53d6\u4e0a\u4e00\u6bb5\u5165\u4f4d\u6cb9\u91cf',
    next_leg_previous_fob: '\u53d6\u4e0b\u4e00\u6bb5\u52a0\u6cb9\u524d\u5b58\u91cf',
    flight_no_prefix: '\u822a\u73ed\u53f7\u524d\u7f00\u89c4\u8303\u5316',
};

const STATUS_META = {
    pending: { label: TEXT.pending, color: 'default', icon: <ClockCircleOutlined /> },
    recognizing: { label: TEXT.recognizing, color: 'processing', icon: <FileSearchOutlined /> },
    parsed: { label: TEXT.parsed, color: 'success', icon: <CheckCircleOutlined /> },
    review_required: { label: TEXT.reviewRequired, color: 'warning', icon: <ExclamationCircleOutlined /> },
    failed: { label: TEXT.failed, color: 'error', icon: <ExclamationCircleOutlined /> },
    confirmed: { label: TEXT.confirmed, color: 'green', icon: <CheckCircleOutlined /> },
    rejected: { label: TEXT.rejectedStatus, color: 'default', icon: <ExclamationCircleOutlined /> },
};

const statusOptions = Object.entries(STATUS_META).map(([value, meta]) => ({
    value,
    label: meta.label,
}));

// T14: \u8bc6\u522b\u9875 "\u6765\u6e90" \u8868\u793a\u3002
//   manual_upload  \u9ed8\u8ba4\u8272 = \u4eba\u5de5\u4e0a\u4f20\uff08\u5386\u53f2 / \u4e3b\u6d41\u8def\u5f84\uff09
//   external_crawl \u84dd\u8272      = \u91c7\u96c6\u5668\u81ea\u52a8\u63a5\u5165\uff08\u672c\u671f\u65b0\u589e\uff09
const SOURCE_TYPE_META = {
    manual_upload: { label: TEXT.sourceManualUpload, color: 'default' },
    external_crawl: { label: TEXT.sourceExternalCrawl, color: 'blue' },
};

const sourceTypeOptions = Object.entries(SOURCE_TYPE_META).map(([value, meta]) => ({
    value,
    label: meta.label,
}));

const normalizeListResult = (result) => {
    const payload = result?.data || result || {};
    return {
        items: payload.items || payload.records || payload.data || [],
        total: payload.total || payload.count || 0,
    };
};

const getErrorMessage = (err) => {
    const detail = err?.response?.data?.detail;
    if (typeof detail === 'string') return detail;
    return detail?.message || err?.response?.data?.message || err.message;
};

const isSupportedFile = (file) => {
    const name = file?.name || '';
    const lowerName = name.toLowerCase();
    return ALLOWED_EXT.some((ext) => lowerName.endsWith(ext))
        || file?.type === 'application/pdf'
        || file?.type?.startsWith('image/');
};

const validateFile = (file) => {
    if (!isSupportedFile(file)) {
        return { ok: false, reason: TEXT.invalidFileType };
    }
    if (file.size > MAX_SIZE_MB * 1024 * 1024) {
        return { ok: false, reason: TEXT.invalidFileSize };
    }
    return { ok: true };
};

const formatTime = (value) => value || '-';
const { TextArea } = Input;

/**
 * 在浏览器端用 Web Crypto API 算文件 SHA256（hex 字符串）。
 *
 * 跟后端 ``hashlib.sha256(content).hexdigest()`` 完全一致，所以前后端
 * 算出来的 hash 才能对得上。
 *
 * 注意：``crypto.subtle`` 仅在 secure context（https / localhost）下可用。
 * 生产域名是 https 的话没问题；本地 localhost 也 OK；裸 http 内网部署会失败，
 * 这种场景会 throw，外层捕获后 fallback 到「直接上传 + 事后看 dup」的旧路径。
 */
const sha256HexFromFile = async (file) => {
    const buf = await file.arrayBuffer();
    const digest = await crypto.subtle.digest('SHA-256', buf);
    return Array.from(new Uint8Array(digest))
        .map((b) => b.toString(16).padStart(2, '0'))
        .join('');
};

const isImageSource = (record) => {
    const contentType = record?.content_type || '';
    const fileName = (record?.file_name || record?.fileName || '').toLowerCase();
    return contentType.startsWith('image/')
        || ['.gif', '.jpg', '.jpeg', '.png', '.webp'].some((ext) => fileName.endsWith(ext));
};

// 字段顺序对齐 9H-FLM MVT 结算 Excel（详见 backend/db_migrations/108）。
// 列表/抽屉/导出三处统一用这个顺序，避免业务侧再问"为啥不一样"。
const REVIEW_FIELDS = [
    ['flight_log_no', 'TL No./Flight Log No.'],
    ['msn', 'MSN'],
    // S1: A/C Reg \u4ece\u8f93\u5165\u6846\u6539\u4e3a\u673a\u961f\u4e0b\u62c9\uff08fleet \u7c7b\u578b\uff09
    ['aircraft_reg', 'A/C Reg', 'fleet'],
    ['flight_no', 'Flight No.'],
    ['date_utc', 'Date UTC'],
    ['off_chocks_utc', 'Off Chocks'],
    ['takeoff_utc', 'Takeoff'],
    ['landing_utc', 'Landing'],
    ['on_chocks_utc', 'On Chocks'],
    ['from_iata', 'From IATA'],
    ['from_icao', 'From ICAO'],
    ['to_iata', 'To IATA'],
    ['to_icao', 'To ICAO'],
    ['flight_hours', 'Flight Hours'],
    ['block_hours', 'Block Hours'],
    // 燃油 7 个字段，顺序 = MVT Excel
    ['off_block_fuel_kg', 'Fuel KG', 'number'],
    ['fuel_liter', 'Fuel Liter', 'number'],
    ['fuel_density', 'Fuel Density', 'number'],
    ['actual_uplift_kg', 'Fuel Uplift', 'number'],
    ['previous_fuel_onboard_kg', 'Previous Fuel Onboard', 'number'],
    ['fuel_check_kg', 'Fuel Check KG', 'number'],
    ['on_block_fuel_kg', 'On Block Fuel KG (DB only)', 'number'],
    ['major_maintenance', TEXT.majorMaintenance, 'text'],
    ['incidents_observations', 'Incidents / Observations (DB only)', 'text'],
];

const buildReviewInitialValues = (record = {}) => {
    const values = {};
    REVIEW_FIELDS.forEach(([name]) => {
        values[name] = record[name] ?? record.extracted_data?.[name] ?? record.recognition_result?.[name] ?? null;
    });
    values.review_notes = record.review_notes || '';
    return values;
};

const getReviewReasonText = (record = {}) => {
    if (record.review_reason_text) return record.review_reason_text;
    const reasons = Array.isArray(record.review_reasons) ? record.review_reasons : [];
    if (!reasons.length) return '';
    return reasons.map((reason) => reason.message || reason.field_label || reason.field || reason.code).filter(Boolean).join('\uff1b');
};

const getToolTrace = (record = {}) => (
    record.recognition_result?._tool_trace
    || record.extracted_data?._tool_trace
    || null
);

// S1-S5: \u8bfb\u53d6\u67d0\u5b57\u6bb5\u7684\u63a8\u7b97\u6765\u6e90\u6807\u8bb0\uff08recognition_result._reconciliation[field]\uff09\u3002
const getFieldMark = (record = {}, field) => {
    const recon = record.recognition_result?._reconciliation;
    if (!recon || typeof recon !== 'object') return null;
    const mark = recon[field];
    return mark && typeof mark === 'object' ? mark : null;
};

// \u201c\u63a8\u201d\u6807\u7b7e\u7684\u60ac\u6d6e\u63d0\u793a\u6587\u672c\uff1a\u6765\u6e90\u7c7b\u578b + \u4f9d\u636e\u7b97\u5f0f / \u539f\u503c / \u6765\u6e90\u8bb0\u5f55 ID\u3002
const buildMarkTooltip = (mark) => {
    if (!mark) return '';
    const parts = [RECON_SOURCE_LABELS[mark.source] || mark.source];
    if (mark.from) parts.push(`\u539f\u503c: ${mark.from}`);
    if (mark.basis) parts.push(`\u4f9d\u636e: ${mark.basis}`);
    if (mark.source_record_id) parts.push(`\u6765\u6e90\u8bb0\u5f55: ${mark.source_record_id}`);
    if (Array.isArray(mark.flags) && mark.flags.length) parts.push(`\u6807\u8bb0: ${mark.flags.join(', ')}`);
    return parts.join('\uff1b');
};

// T15: \u5217\u8868\u5355\u5143\u683c\u6e32\u67d3 \u2014\u2014 \u5e26 _reconciliation \u6807\u8bb0\u7684\u5b57\u6bb5\u5728\u503c\u65c1\u8ddf\u4e00\u4e2a\u201c\u63a8\u201d\u6807\u7b7e\uff08\u60ac\u6d6e\u663e\u793a\u6765\u6e90\uff09\uff0c
// \u4e0e\u8be6\u60c5\u62bd\u5c49\u98ce\u683c\u4e00\u81f4\uff1b\u65e0\u6807\u8bb0\u65f6\u539f\u6837\u8fd4\u56de\u503c\uff08\u4e0d\u589e\u52a0\u4efb\u4f55 DOM\uff09\u3002
const renderWithMark = (content, record, field) => {
    const mark = getFieldMark(record, field);
    if (!mark) return content;
    return (
        <Space size={4}>
            <span>{content}</span>
            <Tooltip title={buildMarkTooltip(mark)}>
                <Tag color="purple" style={{ margin: 0, lineHeight: '16px', padding: '0 4px' }}>
                    {TEXT.derivedTag}
                </Tag>
            </Tooltip>
        </Space>
    );
};

const FlightLogRecognitionPanel = () => {
    const [filters, setFilters] = useState({
        keyword: '',
        status: undefined,
        source_type: undefined,
        // 机号：CSV 多值（逗号分隔），空串 = 不过滤；与后端 aircraft_reg 参数一一对应。
        aircraft_reg: '',
        dateRange: null,
    });
    const [pagination, setPagination] = useState({ current: 1, pageSize: 20 });
    // 列表排序：sort_by 指定排序字段（date_utc / off_chocks_utc / flight_log_no），
    // sort_order 指定方向（asc / desc，默认 desc = 最近的在最前）。
    // 三列表头排序器受控，仅影响展示；导出始终走后端默认升序。
    const [sortBy, setSortBy] = useState('date_utc');
    const [sortOrder, setSortOrder] = useState('desc');
    const [data, setData] = useState([]);
    const [total, setTotal] = useState(0);
    const [loading, setLoading] = useState(false);
    const [uploading, setUploading] = useState(false);
    const [uploadResultOpen, setUploadResultOpen] = useState(false);
    // uploadResult 持久化到 sessionStorage：关弹窗后还可以点「上次上传结果」按钮重新打开。
    // sessionStorage 的生命周期 = 浏览器标签页，关 tab 才丢，刷新页面还在，
    // 比 useState 的"刷新就丢"+ "弹窗关了就找不回"要好用太多。
    // 不用 localStorage 的原因：避免跨设备/跨 tab 的脏数据混淆复核员视角。
    const [uploadResult, setUploadResultRaw] = useState(() => {
        try {
            const cached = sessionStorage.getItem(UPLOAD_RESULT_STORAGE_KEY);
            return cached ? JSON.parse(cached) : null;
        } catch {
            return null;
        }
    });
    const setUploadResult = useCallback((result) => {
        setUploadResultRaw(result);
        try {
            if (result) {
                sessionStorage.setItem(UPLOAD_RESULT_STORAGE_KEY, JSON.stringify(result));
            } else {
                sessionStorage.removeItem(UPLOAD_RESULT_STORAGE_KEY);
            }
        } catch {
            // 隐私模式 / 配额满 等场景下 sessionStorage 不可用，忽略即可。
        }
    }, []);
    const [detailOpen, setDetailOpen] = useState(false);
    const [detailLoading, setDetailLoading] = useState(false);
    const [reviewSaving, setReviewSaving] = useState(false);
    const [selectedRecord, setSelectedRecord] = useState(null);
    const [sourcePreview, setSourcePreview] = useState(null);
    const [reviewForm] = Form.useForm();
    const [selectedRowKeys, setSelectedRowKeys] = useState([]);
    const [batchDeleting, setBatchDeleting] = useState(false);
    const [exporting, setExporting] = useState(false);
    const [exportingSource, setExportingSource] = useState(false);
    const [reconciling, setReconciling] = useState(false);
    // 去重翻案进行中的记录 id（按钮 loading 用）。
    const [dedupRestoringId, setDedupRestoringId] = useState(null);
    const [unvoidingId, setUnvoidingId] = useState(null);
    // S1: \u5728\u5f79\u673a\u961f\u9009\u9879\uff08\u673a\u53f7\u4e0b\u62c9\uff09\u3002\u53d6\u7a7a\u65f6\u964d\u7ea7\u4e3a\u53ea\u8bfb\u5c55\u793a\u3002
    const [fleetOptions, setFleetOptions] = useState([]);
    const [tableScrollY, setTableScrollY] = useState(() => {
        if (typeof window === 'undefined') return 400;
        const h = window.innerHeight - TABLE_SCROLL_FIXED_OFFSET;
        return h > 200 ? h : 400;
    });

    // 「上传预览」相关：拖文件后先本地算 SHA256 → 查重 → 让用户看清楚再决定上不上。
    // 这是路 B 的核心：把 dup 决策从「事后追溯」前移到「上传前预览」，根本性消除痛点。
    const [preCheckOpen, setPreCheckOpen] = useState(false);
    const [preCheckLoading, setPreCheckLoading] = useState(false);
    // preCheckBatch 形如 [{file: File, hash: string, exists: bool, record: {id,file_name,uploaded_at,status} | null}]
    const [preCheckBatch, setPreCheckBatch] = useState([]);

    const pendingBatchRef = useRef([]);
    const warnedTruncateRef = useRef(false);

    const buildParams = useCallback((pg = pagination, flt = filters, srtBy = sortBy, srt = sortOrder) => {
        const params = {
            page: pg.current,
            page_size: pg.pageSize,
            sort_by: srtBy,
            sort_order: srt,
        };
        if (flt.keyword?.trim()) params.keyword = flt.keyword.trim();
        if (flt.status) params.status = flt.status;
        if (flt.source_type) params.source_type = flt.source_type;
        // 机号：列表 / 导出当前筛选 / 对齐补全三处共用 buildParams，接线一处即三处同口径。
        if (flt.aircraft_reg) params.aircraft_reg = flt.aircraft_reg;
        if (flt.dateRange?.[0]) params.date_start = flt.dateRange[0].format('YYYY-MM-DD');
        if (flt.dateRange?.[1]) params.date_end = flt.dateRange[1].format('YYYY-MM-DD');
        return params;
    }, [filters, pagination, sortBy, sortOrder]);

    const fetchData = useCallback(async (pg = pagination, flt = filters, srtBy = sortBy, srt = sortOrder) => {
        setLoading(true);
        try {
            const res = await flightLogRecognitionApi.list(buildParams(pg, flt, srtBy, srt));
            const result = normalizeListResult(res);
            setData(result.items);
            setTotal(result.total);
        } catch (err) {
            message.error(`${TEXT.listLoadFailed}: ${getErrorMessage(err)}`);
        } finally {
            setLoading(false);
        }
    }, [buildParams, filters, pagination, sortBy, sortOrder]);

    // ── 局部更新工具：避免「单条/批量操作」触发整表刷新（fetchData）。
    //
    // 之前所有「重新识别 / 复核 / 删除」操作完成后都会调 fetchData() 整体刷新，
    // 但 fetchData 默认参数会捕获**当前** filters state（受控 input 里实时同步），
    // 导致用户「输入了搜索关键词但还没点查询」时，触发刷新会无意中把 input 里
    // 的实时输入当作筛选条件用，列表突然按一个用户没"提交"的条件过滤，体感上
    // 就是「无视筛选条件 / 列表突然变了」。
    //
    // 解法：单条/批量操作 → 局部修改 data 数组 + total，不刷整表。这样既不抢
    // 用户的搜索框输入，也保留分页 / 滚动 / 选中行 / 抽屉等上下文。
    const replaceRecord = useCallback((record) => {
        if (!record?.id) return;
        setData((prev) => prev.map((row) => (row.id === record.id ? record : row)));
    }, []);

    const removeRecords = useCallback((ids) => {
        if (!ids?.length) return;
        const idSet = new Set(ids);
        setData((prev) => {
            const next = prev.filter((row) => !idSet.has(row.id));
            // total 按"实际从当前页拿掉的条数"减；后端可能因竞态返回 not_found，
            // 用「实际本地被移除的条数」来减，而不是 ids.length，更精准。
            const removedCount = prev.length - next.length;
            if (removedCount > 0) setTotal((t) => Math.max(0, t - removedCount));
            return next;
        });
    }, []);

    useEffect(() => {
        fetchData();
    }, []); // eslint-disable-line react-hooks/exhaustive-deps

    // S1: \u52a0\u8f7d\u5728\u5f79\u673a\u961f\u9009\u9879\uff08\u673a\u53f7\u4e0b\u62c9\uff09\u3002\u5931\u8d25 / \u4e3a\u7a7a\u65f6\u9759\u9ed8\u964d\u7ea7\u4e3a\u53ea\u8bfb\u3002
    useEffect(() => {
        let cancelled = false;
        (async () => {
            try {
                const res = await flightLogRecognitionApi.fleetOptions();
                const result = res.data || res;
                if (!cancelled) setFleetOptions(result.items || []);
            } catch (_) {
                if (!cancelled) setFleetOptions([]);
            }
        })();
        return () => { cancelled = true; };
    }, []);

    useEffect(() => {
        const updateHeight = () => {
            const h = window.innerHeight - TABLE_SCROLL_FIXED_OFFSET;
            setTableScrollY(h > 200 ? h : 400);
        };
        updateHeight();
        window.addEventListener('resize', updateHeight);
        return () => window.removeEventListener('resize', updateHeight);
    }, []);

    const handleSearch = () => {
        const nextPagination = { ...pagination, current: 1 };
        setPagination(nextPagination);
        fetchData(nextPagination, filters);
    };

    // 下拉/日期类筛选改动后立即查询。显式传 nextFilters/nextPagination，
    // 避免 setState 异步导致 fetchData 拿到旧闭包值。
    const applyFilter = (patch) => {
        const nextFilters = { ...filters, ...patch };
        const nextPagination = { ...pagination, current: 1 };
        setFilters(nextFilters);
        setPagination(nextPagination);
        fetchData(nextPagination, nextFilters);
    };

    const handleReset = () => {
        const nextFilters = { keyword: '', status: undefined, source_type: undefined, aircraft_reg: '', dateRange: null };
        const nextPagination = { current: 1, pageSize: pagination.pageSize };
        setFilters(nextFilters);
        setPagination(nextPagination);
        fetchData(nextPagination, nextFilters);
    };

    const SORT_FIELD_MAP = { dateUtc: 'date_utc', offChocksUtc: 'off_chocks_utc', flightLogNo: 'flight_log_no' };

    const handleTableChange = (pag, _tableFilters, sorter) => {
        const nextSortBy = SORT_FIELD_MAP[sorter?.columnKey] || sortBy;
        const nextSortOrder = sorter?.order === 'descend' ? 'desc' : 'asc';
        const sortChanged = nextSortBy !== sortBy || nextSortOrder !== sortOrder;
        const nextPagination = {
            current: sortChanged ? 1 : pag.current,
            pageSize: pag.pageSize,
        };
        setPagination(nextPagination);
        setSortBy(nextSortBy);
        setSortOrder(nextSortOrder);
        fetchData(nextPagination, filters, nextSortBy, nextSortOrder);
    };

    /**
     * 真正的上传调用：仅在用户从「预检查 Modal」点了「上传」后触发，
     * 或者预检查失败 fallback 时直接调用。
     */
    const doActualUpload = useCallback(async (files) => {
        if (!files.length) return;
        setUploading(true);
        try {
            const res = await flightLogRecognitionApi.upload(files);
            const result = res.data || res;
            setUploadResult(result);
            setUploadResultOpen(true);
            message.success(TEXT.uploadSuccess);
            fetchData({ current: 1, pageSize: pagination.pageSize }, filters);
        } catch (err) {
            message.error(`${TEXT.uploadFailed}: ${getErrorMessage(err)}`);
        } finally {
            setUploading(false);
        }
    }, [fetchData, filters, pagination.pageSize, setUploadResult]);

    /**
     * 拖入文件后的入口：先本地算 SHA256 → 调 check-existence → 弹「上传预览」让用户确认。
     *
     * 容错：crypto.subtle 在非 secure context（裸 http）下不可用，
     * 或者 check-existence 接口异常，都 fallback 到「直接传 + 事后看 dup」的旧路径，
     * 保证可用性兜底。
     */
    const handleUploadSubmit = async (rawFiles) => {
        if (!rawFiles.length) return;
        const files = rawFiles.slice(0, MAX_FILES_PER_BATCH);

        if (typeof crypto === 'undefined' || !crypto?.subtle) {
            await doActualUpload(files);
            return;
        }

        setPreCheckLoading(true);
        setPreCheckOpen(true);
        try {
            const hashed = await Promise.all(
                files.map(async (file) => ({ file, hash: await sha256HexFromFile(file) }))
            );
            const res = await flightLogRecognitionApi.checkExistence(hashed.map((h) => h.hash));
            const result = res.data || res;
            const byHash = new Map((result.items || []).map((it) => [it.hash, it]));
            const merged = hashed.map(({ file, hash }) => {
                const found = byHash.get(hash) || { exists: false, record: null };
                return { file, hash, exists: !!found.exists, record: found.record };
            });
            setPreCheckBatch(merged);
        } catch (err) {
            message.warning(`${TEXT.preCheckFailed}: ${getErrorMessage(err)}`);
            setPreCheckOpen(false);
            setPreCheckBatch([]);
            await doActualUpload(files);
        } finally {
            setPreCheckLoading(false);
        }
    };

    const beforeUpload = (file, fileList) => {
        if (uploading) return Upload.LIST_IGNORE;

        if (fileList.length > MAX_FILES_PER_BATCH && !warnedTruncateRef.current) {
            message.warning(TEXT.batchTruncated);
            warnedTruncateRef.current = true;
        }

        const result = validateFile(file);
        if (result.ok) {
            pendingBatchRef.current.push(file);
        } else {
            message.error(`${file.name}: ${result.reason}`);
        }

        if (file === fileList[fileList.length - 1]) {
            const batch = pendingBatchRef.current;
            pendingBatchRef.current = [];
            warnedTruncateRef.current = false;
            handleUploadSubmit(batch);
        }

        return Upload.LIST_IGNORE;
    };

    const handleOpenDetail = async (record) => {
        setSelectedRecord(record);
        setSourcePreview(null);
        setDetailOpen(true);
        if (!record?.id) return;

        setDetailLoading(true);
        try {
            const [detailRes, previewRes] = await Promise.all([
                flightLogRecognitionApi.detail(record.id),
                flightLogRecognitionApi.signedUrl(record.id),
            ]);
            const detail = detailRes.data || detailRes;
            setSelectedRecord(detail);
            reviewForm.setFieldsValue(buildReviewInitialValues(detail));
            setSourcePreview(previewRes.data || previewRes);
        } catch (err) {
            message.error(`${TEXT.detailLoadFailed}: ${getErrorMessage(err)}`);
        } finally {
            setDetailLoading(false);
        }
    };

    const handleReviewSubmit = async (confirm = false) => {
        if (!selectedRecord?.id) return;

        setReviewSaving(true);
        try {
            const values = await reviewForm.validateFields();
            const { review_notes: reviewNotes, ...fields } = values;
            const res = await flightLogRecognitionApi.review(selectedRecord.id, {
                confirm,
                review_notes: reviewNotes,
                fields,
            });
            const nextRecord = res.data || res;
            setSelectedRecord(nextRecord);
            reviewForm.setFieldsValue(buildReviewInitialValues(nextRecord));
            message.success(confirm ? TEXT.reviewConfirmSuccess : TEXT.reviewSaveSuccess);
            // review 接口已经返回最新 record_to_dict，直接局部替换列表里那一行，不刷整表
            replaceRecord(nextRecord);
        } catch (err) {
            if (err?.errorFields) return;
            message.error(`${TEXT.reviewFailed}: ${getErrorMessage(err)}`);
        } finally {
            setReviewSaving(false);
        }
    };

    const handleReExtract = async (record) => {
        if (!record?.id) return;
        try {
            await flightLogRecognitionApi.reExtract(record.id);
            message.success(TEXT.reExtractQueued);
            // re-extract 接口只返回 {status: 'queued', id}，不带最新 record；
            // 额外 detail 一次拿到最新状态（已变成 pending），局部替换列表那一行。
            // detail 失败不致命，只意味着用户得手动刷新一下，所以静默吞掉。
            try {
                const detailRes = await flightLogRecognitionApi.detail(record.id);
                replaceRecord(detailRes.data || detailRes);
            } catch (_) {
                // 拿不到最新 detail 不影响主流程，列表上那条状态会"延迟"一点，可接受
            }
        } catch (err) {
            message.error(`${TEXT.reExtractFailed}: ${getErrorMessage(err)}`);
        }
    };

    // 人工翻案：把被取代记录改选为去重主记录（清取代标记 + 复活 + 取代同组其余）。
    // 用后端返回的 dedup_promote.superseded_ids 做局部更新（不刷整表，沿用本文件
    // "局部更新避免抢搜索框输入"的口径）。
    const handleDedupRestore = useCallback(async (record) => {
        if (!record?.id) return;
        setDedupRestoringId(record.id);
        try {
            const res = await flightLogRecognitionApi.dedupRestore(record.id);
            const nextRecord = res.data || res;
            const supersededIds = new Set(nextRecord.dedup_promote?.superseded_ids || []);
            setData((prev) => prev.map((row) => {
                if (row.id === nextRecord.id) return nextRecord;
                if (supersededIds.has(row.id)) {
                    return {
                        ...row,
                        status: 'rejected',
                        dedup_superseded: true,
                        superseded_by: nextRecord.id,
                        dedup_manual_main: false,
                    };
                }
                return row;
            }));
            setSelectedRecord((prev) => (prev?.id === nextRecord.id ? nextRecord : prev));
            message.success(TEXT.dedupRestoreSuccess);
        } catch (err) {
            message.error(`${TEXT.dedupRestoreFailed}: ${getErrorMessage(err)}`);
        } finally {
            setDedupRestoringId(null);
        }
    }, []);

    // 作废页人工翻案「取消作废」：清 page_voided 标记 + 从 rejected 复活（状态交后端 recompute
    // 重新判 parsed/review_required），记录重新参与对齐。局部更新本条，不刷整表。
    const handleUnvoid = useCallback(async (record) => {
        if (!record?.id) return;
        setUnvoidingId(record.id);
        try {
            const res = await flightLogRecognitionApi.unvoid(record.id);
            const nextRecord = res.data || res;
            setData((prev) => prev.map((row) => (row.id === nextRecord.id ? nextRecord : row)));
            setSelectedRecord((prev) => (prev?.id === nextRecord.id ? nextRecord : prev));
            message.success(TEXT.unvoidSuccess);
        } catch (err) {
            message.error(`${TEXT.unvoidFailed}: ${getErrorMessage(err)}`);
        } finally {
            setUnvoidingId(null);
        }
    }, []);

    const handleDelete = async (record) => {
        if (!record?.id) return;
        try {
            await flightLogRecognitionApi.remove(record.id);
            message.success(TEXT.deleteSuccess);
            if (selectedRecord?.id === record.id) {
                setDetailOpen(false);
                setSelectedRecord(null);
            }
            setSelectedRowKeys((prev) => prev.filter((id) => id !== record.id));
            // 局部移除（不刷整表）
            removeRecords([record.id]);
        } catch (err) {
            message.error(`${TEXT.deleteFailed}: ${getErrorMessage(err)}`);
        }
    };

    const handleBatchDelete = async () => {
        if (selectedRowKeys.length === 0) return;
        setBatchDeleting(true);
        try {
            const res = await flightLogRecognitionApi.batchRemove(selectedRowKeys);
            const result = res.data || res;
            const deleted = result.deleted_count ?? 0;
            const failed = result.failed_count ?? 0;
            if (failed > 0) {
                message.warning(TEXT.batchDeletePartial(deleted, failed));
            } else {
                message.success(TEXT.batchDeleteSuccess(deleted));
            }
            if (selectedRecord && selectedRowKeys.includes(selectedRecord.id)) {
                setDetailOpen(false);
                setSelectedRecord(null);
            }
            // 用 backend 返回的 items 精确按 status === 'deleted' 移除，
            // 而不是一把按 selectedRowKeys 删；这样若有 not_found（被并发删除/已删除），
            // 行为更精确且 total 更准。
            const deletedIds = (result.items || [])
                .filter((it) => it?.status === 'deleted')
                .map((it) => it.id);
            removeRecords(deletedIds.length ? deletedIds : selectedRowKeys);
            setSelectedRowKeys([]);
        } catch (err) {
            message.error(`${TEXT.batchDeleteFailed}: ${getErrorMessage(err)}`);
        } finally {
            setBatchDeleting(false);
        }
    };

    /**
     * 触发文件下载：用 <a download> 走异步触发，避免被浏览器拦截弹窗。
     * 优先用 file_name 作为下载名（hint），实际名以服务端 OSS Content-Disposition 为准。
     */
    const triggerDownload = (downloadUrl, fileName) => {
        if (!downloadUrl) return;
        const link = document.createElement('a');
        link.href = downloadUrl;
        if (fileName) link.download = fileName;
        // 部分浏览器需要把 a 元素挂到 DOM 才能触发 click
        link.style.display = 'none';
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
    };

    // S7: \u624b\u52a8\u201c\u5bf9\u9f50/\u8865\u5168\u201d\u3002\u9009\u4e2d\u4e86\u5219\u5bf9\u9009\u4e2d\u8303\u56f4\uff0c\u5426\u5219\u5bf9\u5f53\u524d\u7b5b\u9009\u3002
    const handleReconcile = async () => {
        setReconciling(true);
        try {
            const payload = selectedRowKeys.length > 0
                ? { ids: selectedRowKeys }
                : buildParams(pagination, filters);
            const res = await flightLogRecognitionApi.reconcile(payload);
            const result = res.data || res;
            const changed = result.changed_count ?? 0;
            message.success(changed > 0 ? TEXT.reconcileSuccess(changed) : TEXT.reconcileNoChange);
            // \u5bf9\u9f50\u53ef\u80fd\u6539\u52a8\u591a\u6761\u8bb0\u5f55\uff08\u542b\u90bb\u5f20\uff09\uff0c\u5237\u65b0\u5f53\u524d\u9875\u4ee5\u53cd\u6620\u8865\u5168\u7ed3\u679c\u3002
            fetchData(pagination, filters);
        } catch (err) {
            message.error(`${TEXT.reconcileFailed}: ${getErrorMessage(err)}`);
        } finally {
            setReconciling(false);
        }
    };

    const handleExportFiltered = async () => {
        if (total === 0) {
            message.warning(TEXT.exportEmpty);
            return;
        }
        setExporting(true);
        try {
            const res = await flightLogRecognitionApi.exportFiltered(buildParams(pagination, filters));
            const result = res.data || res;
            triggerDownload(result.download_url, result.file_name);
            message.success(`${TEXT.exportSuccess}: ${result.count ?? '?'}`);
        } catch (err) {
            message.error(`${TEXT.exportFailed}: ${getErrorMessage(err)}`);
        } finally {
            setExporting(false);
        }
    };

    const handleExportBatch = async () => {
        if (selectedRowKeys.length === 0) return;
        setExporting(true);
        try {
            const res = await flightLogRecognitionApi.exportBatch(selectedRowKeys);
            const result = res.data || res;
            triggerDownload(result.download_url, result.file_name);
            message.success(`${TEXT.exportSuccess}: ${result.count ?? selectedRowKeys.length}`);
        } catch (err) {
            message.error(`${TEXT.exportFailed}: ${getErrorMessage(err)}`);
        } finally {
            setExporting(false);
        }
    };

    const handleExportSourceFiles = async (ids) => {
        const targetIds = Array.isArray(ids) && ids.length > 0 ? ids : selectedRowKeys;
        if (!targetIds.length) return;
        setExportingSource(true);
        try {
            const res = await flightLogRecognitionApi.exportSourceFiles(targetIds);
            const result = res.data || res;
            triggerDownload(result.download_url, result.file_name);
            message.success(TEXT.exportSourceSuccess);
        } catch (err) {
            message.error(`${TEXT.exportSourceFailed}: ${getErrorMessage(err)}`);
        } finally {
            setExportingSource(false);
        }
    };

    const columns = useMemo(() => [
        {
            title: TEXT.status,
            dataIndex: 'status',
            width: 96,
            fixed: 'left',
            render: (status = 'pending', row) => {
                const meta = STATUS_META[status] || STATUS_META.pending;
                return (
                    <Space direction="vertical" size={2}>
                        <Tag color={meta.color} icon={meta.icon} style={{ margin: 0 }}>
                            {meta.label}
                        </Tag>
                        {row.dedup_superseded && (
                            <Tooltip title={TEXT.dedupTagTip}>
                                <Tag color="gold" style={{ margin: 0 }}>{TEXT.dedupTagShort}</Tag>
                            </Tooltip>
                        )}
                        {row.page_voided && (
                            <Tooltip title={TEXT.voidedTagTip}>
                                <Tag color="red" style={{ margin: 0 }}>{TEXT.voidedTagShort}</Tag>
                            </Tooltip>
                        )}
                    </Space>
                );
            },
        },
        {
            // T14: \u6765\u6e90\u5217\u3002\u53d6 source_type \u5b57\u6bb5\u3002
            //   - manual_upload  \u9ed8\u8ba4\u8272 = \u5386\u53f2 / \u4eba\u5de5\u4e0a\u4f20\u4e3b\u6d41\u8def\u5f84
            //   - external_crawl \u84dd\u8272      = \u91c7\u96c6\u5668\u81ea\u52a8\u63a5\u5165\uff08\u672c\u671f\u65b0\u589e\uff09
            // \u540e\u7aef migration 119 \u8bbe\u4e86 default 'manual_upload'\uff0c\u5b58\u91cf\u4e0d\u4f1a\u4e3a\u7a7a\uff1b
            // \u4e07\u4e00\u4e3a\u7a7a\u4e5f\u4ee5 manual_upload \u5151\u5e95\u3002
            title: TEXT.sourceColumn,
            dataIndex: 'source_type',
            width: 96,
            fixed: 'left',
            render: (sourceType) => {
                const meta = SOURCE_TYPE_META[sourceType] || SOURCE_TYPE_META.manual_upload;
                return (
                    <Tag color={meta.color} style={{ margin: 0 }}>
                        {meta.label}
                    </Tag>
                );
            },
        },
        {
            title: TEXT.reviewReason,
            dataIndex: 'review_reason_text',
            width: 180,
            ellipsis: true,
            render: (value, row) => {
                if (row.status !== 'review_required') return '-';
                const text = value || getReviewReasonText(row) || TEXT.noReviewReason;
                return (
                    <Tooltip title={text}>
                        <Text type="warning" ellipsis style={{ maxWidth: 160 }}>
                            {text}
                        </Text>
                    </Tooltip>
                );
            },
        },
        {
            title: 'TL No.',
            dataIndex: 'flightLogNo',
            key: 'flightLogNo',
            width: 120,
            fixed: 'left',
            sorter: true,
            sortOrder: sortBy === 'flight_log_no' ? (sortOrder === 'desc' ? 'descend' : 'ascend') : null,
            sortDirections: ['ascend', 'descend'],
            render: (value, row) => value || row.flight_log_no || '-',
        },
        {
            title: TEXT.sourceFile,
            dataIndex: 'fileName',
            width: 220,
            ellipsis: true,
            render: (value, row) => (
                <Space size={6}>
                    {(row.content_type || row.file_name || '').includes('image') ? (
                        <FileImageOutlined style={{ color: '#1677ff' }} />
                    ) : (
                        <FilePdfOutlined style={{ color: '#d4380d' }} />
                    )}
                    <Text ellipsis style={{ maxWidth: 180 }}>
                        {value || row.file_name || '-'}
                    </Text>
                </Space>
            ),
        },
        // ─── Excel 字段顺序 (9H-FLM MVT) ───
        // 注意：MSN 列依 Excel 实际填写习惯（决策 1A），内容取 aircraft_reg
        // 不展示列：on_block_fuel_kg / incidents_observations（仅 DB 留存，详情可见）
        { title: 'MSN', dataIndex: 'msn', width: 100, render: (value, row) => renderWithMark(value || row.aircraft_reg || '-', row, 'aircraft_reg') },
        { title: 'Flight No.', dataIndex: 'flightNo', width: 110, render: (value, row) => renderWithMark(value || row.flight_no || row.flight_number || '-', row, 'flight_no') },
        {
            title: 'Date UTC',
            dataIndex: 'dateUtc',
            key: 'dateUtc',
            width: 120,
            sorter: true,
            sortOrder: sortBy === 'date_utc' ? (sortOrder === 'desc' ? 'descend' : 'ascend') : null,
            sortDirections: ['ascend', 'descend'],
            render: (value, row) => value || row.date_utc || row.flight_date || '-',
        },
        {
            title: 'Off Chocks',
            dataIndex: 'offChocksUtc',
            key: 'offChocksUtc',
            width: 110,
            sorter: true,
            sortOrder: sortBy === 'off_chocks_utc' ? (sortOrder === 'desc' ? 'descend' : 'ascend') : null,
            sortDirections: ['ascend', 'descend'],
            render: (value, row) => renderWithMark(formatTime(value || row.off_chocks_utc), row, 'off_chocks_utc'),
        },
        { title: 'Takeoff', dataIndex: 'takeoffUtc', width: 96, render: (value, row) => renderWithMark(formatTime(value || row.takeoff_utc), row, 'takeoff_utc') },
        { title: 'Landing', dataIndex: 'landingUtc', width: 96, render: (value, row) => renderWithMark(formatTime(value || row.landing_utc), row, 'landing_utc') },
        { title: 'On Chocks', dataIndex: 'onChocksUtc', width: 110, render: (value, row) => renderWithMark(formatTime(value || row.on_chocks_utc), row, 'on_chocks_utc') },
        { title: 'From IATA', dataIndex: 'fromIata', width: 96, render: (value, row) => renderWithMark(value || row.from_iata || '-', row, 'from_iata') },
        { title: 'From ICAO', dataIndex: 'fromIcao', width: 96, render: (value, row) => renderWithMark(value || row.from_icao || '-', row, 'from_icao') },
        { title: 'To IATA', dataIndex: 'toIata', width: 96, render: (value, row) => renderWithMark(value || row.to_iata || '-', row, 'to_iata') },
        { title: 'To ICAO', dataIndex: 'toIcao', width: 96, render: (value, row) => renderWithMark(value || row.to_icao || '-', row, 'to_icao') },
        { title: 'Flight Hours', dataIndex: 'flightHours', width: 110, render: (value, row) => value || row.flight_hours || '-' },
        { title: 'Block Hours', dataIndex: 'blockHours', width: 110, render: (value, row) => value || row.block_hours || '-' },
        {
            // 把 7 个燃油字段分组到一个表头下，避免列太多视觉混乱；子列顺序 = Excel
            title: 'Fuel',
            children: [
                { title: 'Fuel KG', dataIndex: 'offBlockFuelKg', width: 110, render: (value, row) => value ?? row.off_block_fuel_kg ?? '-' },
                { title: 'Fuel Liter', dataIndex: 'fuelLiter', width: 110, render: (value, row) => value ?? row.fuel_liter ?? '-' },
                { title: 'Fuel Density', dataIndex: 'fuelDensity', width: 110, render: (value, row) => value ?? row.fuel_density ?? '-' },
                { title: 'Fuel Uplift', dataIndex: 'actualUpliftKg', width: 120, render: (value, row) => value ?? row.actual_uplift_kg ?? '-' },
                { title: 'Previous Fuel Onboard', dataIndex: 'previousFuelOnboardKg', width: 150, render: (value, row) => renderWithMark(value ?? row.previous_fuel_onboard_kg ?? '-', row, 'previous_fuel_onboard_kg') },
                { title: 'Fuel Check KG', dataIndex: 'fuelCheckKg', width: 130, render: (value, row) => value ?? row.fuel_check_kg ?? '-' },
            ],
        },
        {
            title: 'Major Maintenance',
            dataIndex: 'majorMaintenance',
            width: 220,
            ellipsis: true,
            render: (value, row) => value || row.major_maintenance || '/',
        },
        {
            title: TEXT.confidence,
            dataIndex: 'confidence',
            width: 90,
            render: (value, row) => {
                const score = value ?? row.extraction_confidence;
                return score == null ? '-' : `${Math.round(score * 100)}%`;
            },
        },
        {
            title: TEXT.actions,
            key: 'actions',
            width: 220,
            fixed: 'right',
            render: (_, record) => (
                <Space size={4}>
                    <Tooltip title={TEXT.view}>
                        <Button type="link" size="small" icon={<EyeOutlined />} onClick={() => handleOpenDetail(record)}>
                            {TEXT.view}
                        </Button>
                    </Tooltip>
                    {(record.status === 'failed' || record.status === 'review_required') && (
                        <Button type="link" size="small" icon={<ReloadOutlined />} onClick={() => handleReExtract(record)}>
                            {TEXT.reExtract}
                        </Button>
                    )}
                    {record.dedup_superseded && (
                        <Popconfirm
                            title={TEXT.dedupRestoreConfirmTitle}
                            description={TEXT.dedupRestoreConfirmDesc}
                            okText={TEXT.dedupRestore}
                            cancelText={TEXT.deleteCancel}
                            onConfirm={() => handleDedupRestore(record)}
                        >
                            <Button type="link" size="small" loading={dedupRestoringId === record.id}>
                                {TEXT.dedupRestore}
                            </Button>
                        </Popconfirm>
                    )}
                    {record.page_voided && (
                        <Popconfirm
                            title={TEXT.unvoidConfirmTitle}
                            description={TEXT.unvoidConfirmDesc}
                            okText={TEXT.unvoid}
                            cancelText={TEXT.deleteCancel}
                            onConfirm={() => handleUnvoid(record)}
                        >
                            <Button type="link" size="small" loading={unvoidingId === record.id}>
                                {TEXT.unvoid}
                            </Button>
                        </Popconfirm>
                    )}
                    <Popconfirm
                        title={TEXT.deleteConfirmTitle}
                        description={TEXT.deleteConfirmDesc}
                        okText={TEXT.deleteOk}
                        cancelText={TEXT.deleteCancel}
                        okButtonProps={{ danger: true }}
                        onConfirm={() => handleDelete(record)}
                    >
                        <Button type="link" size="small" danger icon={<DeleteOutlined />}>
                            {TEXT.deleteAction}
                        </Button>
                    </Popconfirm>
                </Space>
            ),
        },
    ], [selectedRecord?.id, dedupRestoringId, unvoidingId, sortBy, sortOrder]); // eslint-disable-line react-hooks/exhaustive-deps

    const renderUploadResultItem = (item) => {
        const fileName = item.file_name || item.fileName || item.name || '-';
        const dup = item.duplicate === true;
        const rejected = !dup && (item.status === 'rejected' || item.rejected);
        const itemStyle = dup
            ? { background: '#fffbe6', borderLeft: '3px solid #faad14', paddingLeft: 8 }
            : undefined;
        const originalName = item.original_file_name || item.originalFileName;
        const originalUploadedAt = item.original_uploaded_at || item.originalUploadedAt;
        // sv-SE locale 输出 ISO 风格 "YYYY-MM-DD HH:mm:ss"，截取到分钟。
        // 不引入 dayjs，原生 Date 已经够用。
        const formattedUploadedAt = originalUploadedAt
            ? new Date(originalUploadedAt).toLocaleString('sv-SE', { hour12: false }).slice(0, 16)
            : null;
        return (
            <List.Item style={itemStyle}>
                <Space direction="vertical" size={2} style={{ width: '100%' }}>
                    <Space wrap>
                        {dup ? (
                            <ExclamationCircleOutlined style={{ color: '#faad14' }} />
                        ) : rejected ? (
                            <ExclamationCircleOutlined style={{ color: '#ff4d4f' }} />
                        ) : (
                            <CheckCircleOutlined style={{ color: '#52c41a' }} />
                        )}
                        <Text>{fileName}</Text>
                        <Tag color={dup ? 'gold' : rejected ? 'red' : 'blue'}>
                            {dup ? TEXT.duplicate : rejected ? TEXT.rejected : (item.status || TEXT.accepted)}
                        </Tag>
                        {dup && item.status && (
                            <Tag color="default">{`${TEXT.originalStatus}: ${item.status}`}</Tag>
                        )}
                        {item.message && !dup && <Text type="secondary">{item.message}</Text>}
                    </Space>
                    {dup && (originalName || formattedUploadedAt) && (
                        <Space wrap style={{ paddingLeft: 22 }}>
                            <Text type="secondary" style={{ fontSize: 12 }}>
                                {TEXT.duplicateOf}: <Text style={{ fontSize: 12 }}>{originalName || '-'}</Text>
                                {formattedUploadedAt && (
                                    <Text type="secondary" style={{ fontSize: 12 }}>
                                        {`  (${TEXT.uploadedAt} ${formattedUploadedAt})`}
                                    </Text>
                                )}
                            </Text>
                            {item.id && (
                                <Button
                                    type="link"
                                    size="small"
                                    style={{ padding: 0, height: 'auto' }}
                                    onClick={() => {
                                        setUploadResultOpen(false);
                                        handleOpenDetail({ id: item.id });
                                    }}
                                >
                                    {TEXT.viewOriginal}
                                </Button>
                            )}
                        </Space>
                    )}
                </Space>
            </List.Item>
        );
    };

    const uploadItems = uploadResult?.items || uploadResult?.records || [];

    return (
        <div style={{ padding: 24 }}>
            <Card size="small" styles={{ body: { padding: 16 } }}>
                <Space direction="vertical" size={16} style={{ width: '100%' }}>
                    <Dragger
                        name="files"
                        multiple
                        accept={ALLOWED_EXT.join(',')}
                        showUploadList={false}
                        disabled={uploading}
                        beforeUpload={beforeUpload}
                    >
                        <p className="ant-upload-drag-icon"><InboxOutlined /></p>
                        <p className="ant-upload-text">{uploading ? TEXT.uploading : TEXT.uploadText}</p>
                        <p className="ant-upload-hint">{TEXT.uploadHint}</p>
                    </Dragger>

                    {uploadResult && (
                        <Space>
                            <Button
                                size="small"
                                icon={<EyeOutlined />}
                                onClick={() => setUploadResultOpen(true)}
                            >
                                {TEXT.showLastUploadResult}
                            </Button>
                            <Text type="secondary" style={{ fontSize: 12 }}>
                                {`${TEXT.accepted} ${uploadResult.accepted_count ?? 0} / ${TEXT.duplicate} ${uploadResult.duplicate_count ?? 0} / ${TEXT.rejected} ${uploadResult.rejected_count ?? 0}`}
                            </Text>
                        </Space>
                    )}

                    <Space wrap style={{ width: '100%', justifyContent: 'space-between' }}>
                        <Space wrap>
                            <Input
                                allowClear
                                prefix={<SearchOutlined />}
                                placeholder={`TL No. / ${TEXT.flightNo} / ${TEXT.aircraftReg} / ${TEXT.airport} / ${TEXT.fileName}`}
                                value={filters.keyword}
                                onChange={(event) => setFilters((prev) => ({ ...prev, keyword: event.target.value }))}
                                onPressEnter={handleSearch}
                                style={{ width: 260 }}
                            />
                            <Select
                                allowClear
                                placeholder={TEXT.status}
                                options={statusOptions}
                                value={filters.status}
                                onChange={(value) => applyFilter({ status: value })}
                                style={{ width: 130 }}
                            />
                            <Select
                                allowClear
                                placeholder={TEXT.sourceColumn}
                                options={sourceTypeOptions}
                                value={filters.source_type}
                                onChange={(value) => applyFilter({ source_type: value })}
                                style={{ width: 130 }}
                            />
                            <Select
                                // 机号筛选：tags 模式 = 可选在役机队 + 可手输任意机号（含退役/外租），
                                // 支持逗号/空格分词多选；后端 ilike 模糊匹配，只输片段也能命中。
                                mode="tags"
                                allowClear
                                placeholder={TEXT.aircraftRegFilter}
                                popupMatchSelectWidth={false}
                                value={filters.aircraft_reg ? filters.aircraft_reg.split(',') : []}
                                onChange={(values) => applyFilter({
                                    aircraft_reg: (values || []).map((value) => value.trim()).filter(Boolean).join(','),
                                })}
                                options={fleetOptions.map((reg) => ({ value: reg, label: reg }))}
                                maxTagCount="responsive"
                                tokenSeparators={[',', ' ']}
                                style={{ minWidth: 180, maxWidth: 260 }}
                            />
                            <RangePicker
                                value={filters.dateRange}
                                onChange={(value) => applyFilter({ dateRange: value })}
                                style={{ width: 240 }}
                            />
                            <Button type="primary" icon={<SearchOutlined />} onClick={handleSearch}>
                                {TEXT.search}
                            </Button>
                            <Button icon={<ReloadOutlined />} onClick={handleReset}>
                                {TEXT.reset}
                            </Button>
                        </Space>
                        <Space>
                            <Button
                                icon={<ThunderboltOutlined />}
                                onClick={handleReconcile}
                                loading={reconciling}
                            >
                                {reconciling ? TEXT.reconciling : TEXT.reconcile}
                            </Button>
                            <Button
                                icon={<DownloadOutlined />}
                                onClick={handleExportFiltered}
                                loading={exporting}
                                disabled={total === 0}
                            >
                                {TEXT.exportFiltered}
                            </Button>
                        </Space>
                    </Space>

                    {selectedRowKeys.length > 0 && (
                        <Space style={{
                            padding: '8px 12px',
                            background: '#fffbe6',
                            border: '1px solid #ffe58f',
                            borderRadius: 4,
                            width: '100%',
                            justifyContent: 'space-between',
                        }}>
                            <Text>{TEXT.batchDeleteSelected(selectedRowKeys.length)}</Text>
                            <Space>
                                <Button
                                    size="small"
                                    onClick={() => setSelectedRowKeys([])}
                                    disabled={batchDeleting || exporting}
                                >
                                    {TEXT.clearSelection}
                                </Button>
                                <Button
                                    size="small"
                                    icon={<DownloadOutlined />}
                                    onClick={handleExportBatch}
                                    loading={exporting}
                                    disabled={batchDeleting}
                                >
                                    {TEXT.exportBatch}
                                </Button>
                                <Button
                                    size="small"
                                    icon={<DownloadOutlined />}
                                    onClick={() => handleExportSourceFiles(selectedRowKeys)}
                                    loading={exportingSource}
                                    disabled={batchDeleting || exporting}
                                >
                                    {TEXT.exportSourceBatch}
                                </Button>
                                <Popconfirm
                                    title={TEXT.batchDeleteConfirmTitle(selectedRowKeys.length)}
                                    description={TEXT.batchDeleteConfirmDesc}
                                    okText={TEXT.deleteOk}
                                    cancelText={TEXT.deleteCancel}
                                    okButtonProps={{ danger: true, loading: batchDeleting }}
                                    onConfirm={handleBatchDelete}
                                >
                                    <Button
                                        size="small"
                                        danger
                                        type="primary"
                                        icon={<DeleteOutlined />}
                                        loading={batchDeleting}
                                    >
                                        {TEXT.batchDeleteAction}
                                    </Button>
                                </Popconfirm>
                            </Space>
                        </Space>
                    )}

                    <Table
                        rowKey="id"
                        size="small"
                        columns={columns}
                        dataSource={data}
                        loading={loading}
                        scroll={{ x: 2080, y: tableScrollY, scrollToFirstRowOnChange: false }}
                        rowSelection={{
                            selectedRowKeys,
                            onChange: setSelectedRowKeys,
                            // 无 id 的脏数据不允许选（理论不会出现，防御性）
                            getCheckboxProps: (record) => ({ disabled: !record.id }),
                            preserveSelectedRowKeys: true,
                        }}
                        pagination={{
                            current: pagination.current,
                            pageSize: pagination.pageSize,
                            total,
                            showSizeChanger: true,
                            showTotal: (count) => `${TEXT.totalPrefix} ${count} ${TEXT.totalSuffix}`,
                        }}
                        onChange={handleTableChange}
                        locale={{
                            emptyText: <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description={TEXT.emptyRecords} />,
                        }}
                    />
                </Space>
            </Card>

            <Modal
                title={TEXT.preCheckTitle}
                open={preCheckOpen}
                onCancel={() => {
                    if (uploading) return;
                    setPreCheckOpen(false);
                    setPreCheckBatch([]);
                }}
                footer={(() => {
                    const fresh = preCheckBatch.filter((it) => !it.exists);
                    const all = preCheckBatch;
                    return (
                        <Space>
                            <Button
                                onClick={() => {
                                    setPreCheckOpen(false);
                                    setPreCheckBatch([]);
                                }}
                                disabled={uploading}
                            >
                                {TEXT.preCheckCancel}
                            </Button>
                            <Button
                                onClick={async () => {
                                    setPreCheckOpen(false);
                                    const files = all.map((it) => it.file);
                                    setPreCheckBatch([]);
                                    await doActualUpload(files);
                                }}
                                disabled={uploading || preCheckLoading || all.length === 0}
                            >
                                {TEXT.preCheckUploadAll(all.length)}
                            </Button>
                            <Button
                                type="primary"
                                loading={uploading}
                                disabled={preCheckLoading || fresh.length === 0}
                                onClick={async () => {
                                    setPreCheckOpen(false);
                                    const files = fresh.map((it) => it.file);
                                    setPreCheckBatch([]);
                                    await doActualUpload(files);
                                }}
                            >
                                {TEXT.preCheckUploadFresh(fresh.length)}
                            </Button>
                        </Space>
                    );
                })()}
                width={620}
                destroyOnClose
            >
                <Spin spinning={preCheckLoading} tip={TEXT.preCheckLoading}>
                    {!preCheckLoading && preCheckBatch.length > 0 && (() => {
                        const fresh = preCheckBatch.filter((it) => !it.exists);
                        const dup = preCheckBatch.filter((it) => it.exists);
                        return (
                            <Space direction="vertical" size={12} style={{ width: '100%' }}>
                                <Space wrap>
                                    <Tag color="blue">
                                        {TEXT.preCheckSummary(preCheckBatch.length, fresh.length, dup.length)}
                                    </Tag>
                                </Space>
                                {fresh.length > 0 && (
                                    <div>
                                        <Text strong>{TEXT.preCheckFreshSection} ({fresh.length})</Text>
                                        <List
                                            size="small"
                                            dataSource={fresh}
                                            style={{ maxHeight: 180, overflow: 'auto' }}
                                            renderItem={(it) => (
                                                <List.Item>
                                                    <Space>
                                                        <CheckCircleOutlined style={{ color: '#52c41a' }} />
                                                        <Text>{it.file.name}</Text>
                                                    </Space>
                                                </List.Item>
                                            )}
                                        />
                                    </div>
                                )}
                                {dup.length > 0 && (
                                    <div>
                                        <Text strong>{TEXT.preCheckDupSection} ({dup.length})</Text>
                                        <List
                                            size="small"
                                            dataSource={dup}
                                            style={{ maxHeight: 220, overflow: 'auto' }}
                                            renderItem={(it) => {
                                                const r = it.record || {};
                                                const formattedAt = r.uploaded_at
                                                    ? new Date(r.uploaded_at).toLocaleString('sv-SE', { hour12: false }).slice(0, 16)
                                                    : null;
                                                return (
                                                    <List.Item style={{
                                                        background: '#fffbe6',
                                                        borderLeft: '3px solid #faad14',
                                                        paddingLeft: 8,
                                                    }}>
                                                        <Space direction="vertical" size={2} style={{ width: '100%' }}>
                                                            <Space wrap>
                                                                <ExclamationCircleOutlined style={{ color: '#faad14' }} />
                                                                <Text>{it.file.name}</Text>
                                                                {r.status && <Tag color="default">{r.status}</Tag>}
                                                            </Space>
                                                            <Space wrap style={{ paddingLeft: 22 }}>
                                                                <Text type="secondary" style={{ fontSize: 12 }}>
                                                                    {TEXT.duplicateOf}: {r.file_name || '-'}
                                                                    {formattedAt && `  (${TEXT.uploadedAt} ${formattedAt})`}
                                                                </Text>
                                                                {r.id && (
                                                                    <Button
                                                                        type="link"
                                                                        size="small"
                                                                        style={{ padding: 0, height: 'auto' }}
                                                                        onClick={() => {
                                                                            setPreCheckOpen(false);
                                                                            handleOpenDetail({ id: r.id });
                                                                        }}
                                                                    >
                                                                        {TEXT.viewOriginal}
                                                                    </Button>
                                                                )}
                                                            </Space>
                                                        </Space>
                                                    </List.Item>
                                                );
                                            }}
                                        />
                                    </div>
                                )}
                            </Space>
                        );
                    })()}
                </Spin>
            </Modal>

            <Modal
                title={TEXT.uploadResult}
                open={uploadResultOpen}
                onCancel={() => setUploadResultOpen(false)}
                footer={<Button type="primary" onClick={() => setUploadResultOpen(false)}>{TEXT.close}</Button>}
                width={560}
            >
                <Space direction="vertical" style={{ width: '100%' }}>
                    <Space wrap>
                        <Tag color="blue">{`${TEXT.accepted}: ${uploadResult?.accepted_count ?? uploadResult?.acceptedCount ?? 0}`}</Tag>
                        <Tag color="gold">{`${TEXT.duplicate}: ${uploadResult?.duplicate_count ?? uploadResult?.duplicateCount ?? 0}`}</Tag>
                        <Tag color="red">{`${TEXT.rejected}: ${uploadResult?.rejected_count ?? uploadResult?.rejectedCount ?? 0}`}</Tag>
                    </Space>
                    <List
                        size="small"
                        dataSource={uploadItems}
                        renderItem={renderUploadResultItem}
                        style={{ maxHeight: 360, overflow: 'auto' }}
                    />
                </Space>
            </Modal>

            <Drawer
                title={TEXT.drawerTitle}
                width={760}
                open={detailOpen}
                onClose={() => {
                    setDetailOpen(false);
                    setSelectedRecord(null);
                    setSourcePreview(null);
                    reviewForm.resetFields();
                }}
                destroyOnClose
            >
                <Spin spinning={detailLoading}>
                    {selectedRecord ? (
                        <Space direction="vertical" size={16} style={{ width: '100%' }}>
                            {selectedRecord.status === 'review_required' && (
                                <Alert
                                    showIcon
                                    type="warning"
                                    message={TEXT.reviewReason}
                                    description={getReviewReasonText(selectedRecord) || TEXT.noReviewReason}
                                />
                            )}
                            {selectedRecord.dedup_superseded && (
                                <Alert
                                    showIcon
                                    type="warning"
                                    message={TEXT.dedupTag}
                                    description={(
                                        <Space direction="vertical" size={4}>
                                            <Text>{TEXT.dedupDrawerAlert}</Text>
                                            <Space>
                                                {selectedRecord.superseded_by && (
                                                    <Button
                                                        type="link"
                                                        size="small"
                                                        style={{ padding: 0, height: 'auto' }}
                                                        onClick={() => handleOpenDetail({ id: selectedRecord.superseded_by })}
                                                    >
                                                        {TEXT.dedupGotoMain}
                                                    </Button>
                                                )}
                                                <Popconfirm
                                                    title={TEXT.dedupRestoreConfirmTitle}
                                                    description={TEXT.dedupRestoreConfirmDesc}
                                                    okText={TEXT.dedupRestore}
                                                    cancelText={TEXT.deleteCancel}
                                                    onConfirm={() => handleDedupRestore(selectedRecord)}
                                                >
                                                    <Button
                                                        type="link"
                                                        size="small"
                                                        style={{ padding: 0, height: 'auto' }}
                                                        loading={dedupRestoringId === selectedRecord.id}
                                                    >
                                                        {TEXT.dedupRestore}
                                                    </Button>
                                                </Popconfirm>
                                            </Space>
                                        </Space>
                                    )}
                                />
                            )}
                            {selectedRecord.page_voided && (
                                <Alert
                                    showIcon
                                    type="error"
                                    message={TEXT.voidedTag}
                                    description={(
                                        <Space direction="vertical" size={4}>
                                            <Text>{TEXT.voidedDrawerAlert}</Text>
                                            <Popconfirm
                                                title={TEXT.unvoidConfirmTitle}
                                                description={TEXT.unvoidConfirmDesc}
                                                okText={TEXT.unvoid}
                                                cancelText={TEXT.deleteCancel}
                                                onConfirm={() => handleUnvoid(selectedRecord)}
                                            >
                                                <Button
                                                    type="link"
                                                    size="small"
                                                    style={{ padding: 0, height: 'auto' }}
                                                    loading={unvoidingId === selectedRecord.id}
                                                >
                                                    {TEXT.unvoid}
                                                </Button>
                                            </Popconfirm>
                                        </Space>
                                    )}
                                />
                            )}
                            {/* T14: external_crawl \u6765\u6e90 \u2192 \u987d\u90e8\u4fe1\u606f\u533a\u663e\u793a\u6293\u53d6\u8bb0\u5f55\u53ef\u70b9\u51fb\u94fe\u63a5\u3002
                                manual_upload \u4ec0\u4e48\u90fd\u4e0d\u663e\u793a\u3002\u62bd\u5c49\u4e0a\u4e0b\u6587\u91cc\u91cd\u8c03\u91c7\u96c6\u4fe1\u606f\u3002 */}
                            {selectedRecord.source_type === 'external_crawl' && selectedRecord.external_crawl_file && (
                                <Alert
                                    showIcon
                                    type="info"
                                    message={TEXT.sourceCrawlInfoTitle}
                                    description={(
                                        <a
                                            href={`/settlement/external-crawl-config?config_id=${selectedRecord.external_crawl_file.url_config_id}&file_id=${selectedRecord.external_crawl_file.id}`}
                                            target="_blank"
                                            rel="noopener noreferrer"
                                        >
                                            {TEXT.sourceCrawlLinkText(
                                                selectedRecord.external_crawl_file.id,
                                                selectedRecord.external_crawl_file.site_key,
                                                selectedRecord.external_crawl_file.date_key,
                                            )}
                                        </a>
                                    )}
                                />
                            )}
                            {selectedRecord.source_type === 'external_crawl'
                                && selectedRecord.external_crawl_file_id
                                && !selectedRecord.external_crawl_file && (
                                    <Alert
                                        showIcon
                                        type="warning"
                                        message={TEXT.sourceCrawlInfoTitle}
                                        description={TEXT.sourceCrawlDeleted}
                                    />
                                )}
                            {(() => {
                                const trace = getToolTrace(selectedRecord);
                                return trace && trace.length > 0 ? (
                                    <ToolTraceViewer trace={trace} />
                                ) : null;
                            })()}
                            <Card size="small" title={TEXT.sourcePreview}>
                                {sourcePreview?.signed_url ? (
                                    isImageSource(selectedRecord) ? (
                                        <Image
                                            src={sourcePreview.signed_url}
                                            alt={selectedRecord.file_name || selectedRecord.fileName || 'Flight Log'}
                                            style={{ width: '100%', maxHeight: 720, objectFit: 'contain' }}
                                        />
                                    ) : (
                                        <iframe
                                            title={selectedRecord.file_name || selectedRecord.fileName || 'Flight Log'}
                                            src={sourcePreview.signed_url}
                                            style={{
                                                width: '100%',
                                                height: 720,
                                                border: '1px solid #f0f0f0',
                                                borderRadius: 4,
                                            }}
                                        />
                                    )
                                ) : (
                                    <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description={TEXT.previewLoadFailed} />
                                )}
                            </Card>
                            <Card
                                size="small"
                                title={TEXT.reviewFields}
                                extra={selectedRecord.status === 'rejected' ? (
                                    // 已拒绝（去重被取代 / 作废页）是终态：不给保存/确认入口，
                                    // 避免把 rejected 记录裸翻 review_required 或确认成 confirmed 却
                                    // 仍带标记的矛盾态。复活只能走对应翻案入口（作废页「取消作废」、
                                    // 去重被取代「设为主记录」）。
                                    <Text type="secondary" style={{ fontSize: 12 }}>
                                        {selectedRecord.page_voided ? TEXT.voidedReviewHint : TEXT.rejectedReviewHint}
                                    </Text>
                                ) : (
                                    <Space>
                                        <Button loading={reviewSaving} onClick={() => handleReviewSubmit(false)}>
                                            {TEXT.saveReview}
                                        </Button>
                                        <Button type="primary" loading={reviewSaving} onClick={() => handleReviewSubmit(true)}>
                                            {TEXT.confirmReview}
                                        </Button>
                                    </Space>
                                )}
                            >
                                <Form form={reviewForm} layout="vertical" size="small">
                                    <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, minmax(0, 1fr))', gap: '0 12px' }}>
                                        {REVIEW_FIELDS.map(([name, label, type]) => {
                                            // S1-S5: \u5e26\u63a8\u7b97\u6807\u8bb0\u7684\u5b57\u6bb5\u5728 label \u65c1\u663e\u793a\u201c\u63a8\u201d\u6807\u7b7e + \u6765\u6e90\u60ac\u6d6e\u3002
                                            const mark = getFieldMark(selectedRecord, name);
                                            const fieldLabel = mark ? (
                                                <Space size={4}>
                                                    {label}
                                                    <Tooltip title={buildMarkTooltip(mark)}>
                                                        <Tag color="purple" style={{ margin: 0, lineHeight: '16px' }}>
                                                            {TEXT.derivedTag}
                                                        </Tag>
                                                    </Tooltip>
                                                </Space>
                                            ) : label;
                                            return (
                                                <Form.Item
                                                    key={name}
                                                    name={name}
                                                    label={fieldLabel}
                                                    style={{ gridColumn: type === 'text' ? '1 / span 2' : undefined }}
                                                >
                                                    {type === 'number' ? (
                                                        <InputNumber style={{ width: '100%' }} precision={2} />
                                                    ) : type === 'text' ? (
                                                        <TextArea autoSize={{ minRows: 2, maxRows: 5 }} />
                                                    ) : type === 'fleet' ? (
                                                        // \u673a\u53f7\u4e0b\u62c9\uff1a\u9009\u9879\u6765\u81ea\u5728\u5f79\u673a\u961f\uff1b\u53d6\u4e0d\u5230\u9009\u9879\u65f6\u5141\u8bb8\u81ea\u7531\u8f93\u5165\uff08\u53ea\u8bfb\u5c55\u793a\u5df2\u6709\u503c\uff09\u3002
                                                        <Select
                                                            showSearch
                                                            allowClear
                                                            placeholder="A/C Reg"
                                                            options={fleetOptions.map((reg) => ({ value: reg, label: reg }))}
                                                            notFoundContent={fleetOptions.length === 0 ? TEXT.fleetEmptyTip : undefined}
                                                        />
                                                    ) : (
                                                        <Input />
                                                    )}
                                                </Form.Item>
                                            );
                                        })}
                                        <Form.Item name="review_notes" label={TEXT.reviewNotes} style={{ gridColumn: '1 / span 2' }}>
                                            <TextArea autoSize={{ minRows: 2, maxRows: 4 }} />
                                        </Form.Item>
                                    </div>
                                </Form>
                            </Card>
                            <Card size="small" title={TEXT.rawResult}>
                                <pre style={{ whiteSpace: 'pre-wrap', margin: 0 }}>
                                    {JSON.stringify(selectedRecord.recognition_result || selectedRecord.extracted_data || selectedRecord, null, 2)}
                                </pre>
                            </Card>
                        </Space>
                    ) : null}
                </Spin>
            </Drawer>
        </div>
    );
};

export default FlightLogRecognitionPanel;
